import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, Check, ClipboardCheck, RotateCcw, Loader2,
  AlertTriangle, Archive, Trophy, Building2, Calendar, Banknote, Info, ChevronDown, ChevronUp,
} from "lucide-react";

const COLOR_PILL = {
  red:         { bg: "bg-warn-soft", text: "text-warn", border: "border-warn/40" },
  orange:      { bg: "bg-warn-soft", text: "text-warn", border: "border-warn/40" },
  amber:       { bg: "bg-warn-soft", text: "text-warn", border: "border-warn/40" },
  light_green: { bg: "bg-good-soft", text: "text-good", border: "border-good/40" },
  dark_green:  { bg: "bg-good-soft", text: "text-good", border: "border-good/40" },
};
const COLOR_BAR = {
  red: "bg-warn", orange: "bg-warn", amber: "bg-warn",
  light_green: "bg-good", dark_green: "bg-good",
};
const PILL_FOR = (c) => COLOR_PILL[c] || COLOR_PILL.amber;
const BAR_FOR  = (c) => COLOR_BAR[c]  || "bg-brand";

const E = {
  schema:   () => `/tapi/schema`,
  context:  (tid) => `/tapi/context/${tid}`,
  get:      (tid) => `/tapi/${tid}`,
  start:    (tid) => `/tapi/${tid}/start`,
  answers:  (tid) => `/tapi/${tid}/answers`,
  submit:   (tid) => `/tapi/${tid}/submit`,
  retake:   (tid) => `/tapi/${tid}/retake`,
};

export default function TapiModule({ tenderId }) {
  const [schema, setSchema] = useState(null);
  const [ctx, setCtx] = useState(null);
  const [assessment, setAssessment] = useState(null);
  const [archivedCount, setArchivedCount] = useState(0);
  const [view, setView] = useState("loading");
  const [qIdx, setQIdx] = useState(0);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showRetakeModal, setShowRetakeModal] = useState(false);
  const [contextCollapsed, setContextCollapsed] = useState(false);

  useEffect(() => {
    (async () => {
      if (!tenderId) return;
      try {
        const [{ data: sch }, { data: c }, { data: latest }] = await Promise.all([
          api.get(E.schema()),
          api.get(E.context(tenderId)),
          api.get(E.get(tenderId)),
        ]);
        setSchema(sch); setCtx(c);
        setArchivedCount(latest.archived_count || 0);
        if (latest.assessment) {
          setAssessment(latest.assessment);
          setView(latest.assessment.status === "completed" ? "results" : "gate");
        } else {
          setView("gate");
        }
      } catch (ex) {
        toast.error(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
      }
    })();
  }, [tenderId]);

  const tenderClosed = ctx?.tender?.is_closed === true;
  const currentQ = schema?.questions?.[qIdx];
  const totalQ = schema?.questions?.length || 10;
  const answers = assessment?.answers || {};

  const dimensionQs = useMemo(() => {
    if (!schema || !currentQ) return [];
    return schema.questions.filter((q) => q.dimension === currentQ.dimension);
  }, [schema, currentQ]);
  const dimensionLabel = useMemo(() => {
    if (!schema || !currentQ) return "";
    return schema.dimensions.find((d) => d.id === currentQ.dimension)?.label || "";
  }, [schema, currentQ]);

  // Selection helpers — values are always arrays in storage
  const selectedFor = (code) => {
    const v = answers[code];
    if (Array.isArray(v)) return v;
    if (typeof v === "number") return [v];
    return [];
  };

  const persistAnswer = useCallback(async (code, newArr) => {
    if (!assessment) return;
    const optimistic = { ...(assessment.answers || {}), [code]: newArr };
    setAssessment({ ...assessment, answers: optimistic });
    setSaving(true);
    try {
      const { data } = await api.patch(E.answers(tenderId), { answers: { [code]: newArr } });
      setAssessment(data);
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
    } finally { setSaving(false); }
  }, [assessment, tenderId]);

  const selectSingle = (code, idx) => persistAnswer(code, [idx]);
  const toggleMulti = (code, idx) => {
    const cur = selectedFor(code);
    const next = cur.includes(idx) ? cur.filter((x) => x !== idx) : [...cur, idx].sort((a, b) => a - b);
    persistAnswer(code, next);
  };

  const goNext = () => { if (qIdx < totalQ - 1) setQIdx(qIdx + 1); };
  const goPrev = () => { if (qIdx > 0) setQIdx(qIdx - 1); };

  const startAttempt = async () => {
    try {
      const { data } = await api.post(E.start(tenderId));
      setAssessment(data);
      // Apply suggestions for Q6/Q8 if user has no answer yet
      const sugs = ctx?.suggestions || {};
      const patch = {};
      Object.keys(sugs).forEach((k) => {
        if (typeof sugs[k] === "number" && !(k in (data.answers || {}))) {
          patch[k] = [sugs[k]];
        }
      });
      if (Object.keys(patch).length) {
        try {
          const { data: upd } = await api.patch(E.answers(tenderId), { answers: patch });
          setAssessment(upd);
        } catch { /* ignore — user can still answer manually */ }
      }
      setQIdx(0);
      setView("quiz");
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
    }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post(E.submit(tenderId), { answers: {} });
      setAssessment(data);
      setView("results");
      toast.success(`TAPI complete · ${data.total_score}/100 · ${data.tier_label}`);
    } catch (ex) {
      const detail = ex.response?.data?.detail;
      if (detail?.code === "incomplete" && Array.isArray(detail.missing_questions)) {
        toast.error(`Please answer all required questions. Missing: ${detail.missing_questions.join(", ")}`);
        const idx = (schema.questions || []).findIndex((q) => detail.missing_questions.includes(q.code));
        if (idx >= 0) setQIdx(idx);
      } else if (detail?.code === "tender_not_closed") {
        toast.error(detail.message);
      } else {
        toast.error(formatApiErrorDetail(detail) || ex.message);
      }
    } finally { setSubmitting(false); }
  };

  const retakeConfirmed = async () => {
    setShowRetakeModal(false);
    try {
      const { data } = await api.post(E.retake(tenderId));
      setAssessment(data);
      setArchivedCount((c) => c + 1);
      setQIdx(0);
      setView("quiz");
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
    }
  };

  if (view === "loading") {
    return <div className="text-sm text-ink-muted uppercase tracking-wider" data-testid="tapi-loading">Loading TAPI…</div>;
  }

  // Gate when tender isn't closed yet (post-bid only)
  if (!tenderClosed && view !== "results") {
    return (
      <div className="card-flat p-7 animate-fade-up" data-testid="tapi-not-closed">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-warn shrink-0 mt-0.5" />
          <div>
            <div className="font-display text-xl font-bold tracking-tight">TAPI is post-bid only</div>
            <p className="text-sm text-ink-muted mt-2 max-w-prose leading-relaxed">
              TAPI can be run after the tender closes and the result (L1/L2/.../Disqualified) is known.
              This tender's deadline is <strong className="text-ink font-num">{ctx?.tender?.submission_date || "—"}</strong>.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5 animate-fade-up" data-testid="tapi-module">
      {ctx?.tender && (
        <ContextPanel ctx={ctx} collapsed={contextCollapsed} onToggle={() => setContextCollapsed((v) => !v)} />
      )}

      {view === "gate" && (
        <GateView
          schema={schema}
          assessment={assessment}
          archivedCount={archivedCount}
          onStart={startAttempt}
          onResume={() => { const i = (schema?.questions || []).findIndex((q) => !(q.code in (assessment.answers || {}))); setQIdx(i === -1 ? 0 : i); setView("quiz"); }}
          onViewResult={() => setView("results")}
          onRetake={() => setShowRetakeModal(true)}
        />
      )}

      {view === "quiz" && currentQ && (
        <QuizView
          schema={schema}
          currentQ={currentQ} qIdx={qIdx} totalQ={totalQ}
          dimensionLabel={dimensionLabel} dimensionQs={dimensionQs}
          selectedFor={selectedFor}
          selectSingle={selectSingle} toggleMulti={toggleMulti}
          saving={saving} submitting={submitting}
          goNext={goNext} goPrev={goPrev}
          onSubmit={submit}
          ctx={ctx}
          onJump={(i) => setQIdx(i)}
          answers={answers}
        />
      )}

      {view === "results" && assessment?.status === "completed" && (
        <ResultsView assessment={assessment} schema={schema} onRetake={() => setShowRetakeModal(true)} />
      )}

      {showRetakeModal && (
        <RetakeModal previousScore={assessment?.total_score} previousTier={assessment?.tier_label}
          onConfirm={retakeConfirmed} onClose={() => setShowRetakeModal(false)} />
      )}
    </div>
  );
}

function ContextPanel({ ctx, collapsed, onToggle }) {
  const t = ctx.tender || {};
  const pa = ctx.prior_assessments || {};
  const items = [
    { key: "bnbd", label: "BNBD", info: pa.bnbd },
    { key: "pcei", label: "PCEI", info: pa.pcei },
    { key: "pcab", label: "PCAB", info: pa.pcab },
  ];
  return (
    <div className="card-flat" data-testid="tapi-context-panel">
      <div className="px-6 py-4 border-b border-ink-line flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ClipboardCheck className="w-4 h-4 text-brand" />
          <div className="stat-label">Tender Context</div>
        </div>
        <button onClick={onToggle} data-testid="tapi-context-toggle"
          className="text-xs uppercase tracking-wider text-ink-muted hover:text-ink flex items-center gap-1">
          {collapsed ? <>Expand <ChevronDown className="w-3 h-3" /></> : <>Collapse <ChevronUp className="w-3 h-3" /></>}
        </button>
      </div>
      {collapsed ? (
        <div className="px-6 py-3 text-sm flex flex-wrap items-center gap-x-5 gap-y-1 text-ink-muted">
          <span className="font-medium text-ink truncate max-w-[40%]" title={t.tender_name}>{t.tender_name || "—"}</span>
          <span className="text-xs">·</span>
          <span className="font-num text-xs">{t.client || "—"}</span>
          {items.map((it) => (
            <span key={it.key} className="text-xs uppercase tracking-wider font-num" data-testid={`tapi-ctx-mini-${it.key}`}>
              {it.label}: {it.info ? `${it.info.score}/100` : "—"}
            </span>
          ))}
        </div>
      ) : (
        <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-2 text-sm">
            <div className="flex items-baseline gap-2">
              <span className="text-[11px] uppercase tracking-wider text-ink-muted w-24 shrink-0">Tender</span>
              <span className="font-medium" data-testid="tapi-ctx-tender">{t.tender_name || "—"}</span>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-[11px] uppercase tracking-wider text-ink-muted w-24 shrink-0">Client</span>
              <span className="flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5 text-ink-muted" /> {t.client || "—"}</span>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-[11px] uppercase tracking-wider text-ink-muted w-24 shrink-0">Value</span>
              <span className="flex items-center gap-1.5 font-num"><Banknote className="w-3.5 h-3.5 text-ink-muted" /> {t.tender_value || "—"}</span>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-[11px] uppercase tracking-wider text-ink-muted w-24 shrink-0">Deadline</span>
              <span className="flex items-center gap-1.5 font-num"><Calendar className="w-3.5 h-3.5 text-ink-muted" /> {t.submission_date || "—"}</span>
            </div>
          </div>
          <div className="space-y-2">
            <div className="text-[11px] uppercase tracking-wider text-ink-muted mb-2">Prior Assessments</div>
            {items.map((it) => {
              const pill = it.info ? PILL_FOR(it.info.tier_color) : null;
              return (
                <div key={it.key} className="flex items-center justify-between text-sm" data-testid={`tapi-ctx-${it.key}`}>
                  <span className="font-medium text-ink-muted">{it.label} Score</span>
                  {it.info ? (
                    <span className="flex items-center gap-2">
                      <span className="font-num">{it.info.score}/100</span>
                      <span className={`chip text-[10px] ${pill.bg} ${pill.text} ${pill.border}`}>{it.info.tier_label}</span>
                    </span>
                  ) : (
                    <span className="text-ink-muted text-xs" title={`No ${it.label} assessment exists.`}>—</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function GateView({ schema, assessment, archivedCount, onStart, onResume, onViewResult, onRetake }) {
  const hasDraft = assessment?.status === "in_progress";
  const hasCompleted = assessment?.status === "completed";
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="tapi-gate">
      <div className="lg:col-span-2 card-flat p-7">
        <div className="stat-label">TAPI · Post-Bid Retrospective</div>
        <h2 className="font-display text-3xl font-bold tracking-tight mt-1">Tender Analysis & Performance Indicator</h2>
        <p className="text-sm text-ink-muted mt-2 leading-relaxed max-w-prose">
          10 questions across 3 dimensions. Mix of single-select and multi-select. Estimated 5–8 minutes.
          Captures how well the bid actually performed and how strong your pre-bid diagnostics turned out to be.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-6">
          {(schema?.dimensions || []).map((d) => (
            <div key={d.id} className="border border-ink-line p-3 rounded-sm bg-white">
              <div className="stat-label">{d.label}</div>
              <div className="font-num text-xs text-ink-muted mt-1">max {d.max_score} pts</div>
              <div className="text-[11px] text-ink-muted mt-1.5 leading-snug">{d.description}</div>
            </div>
          ))}
        </div>
        <div className="mt-7 flex flex-wrap items-center gap-3">
          {hasDraft && (
            <button onClick={onResume} data-testid="tapi-resume-btn"
              className="px-5 py-2.5 text-sm bg-brand hover:bg-brand-hover text-white font-medium rounded-sm flex items-center gap-2 transition-colors">
              Resume <ArrowRight className="w-4 h-4" />
            </button>
          )}
          {!hasDraft && !hasCompleted && (
            <button onClick={onStart} data-testid="tapi-start-btn"
              className="px-5 py-2.5 text-sm bg-brand hover:bg-brand-hover text-white font-medium rounded-sm flex items-center gap-2 transition-colors">
              <ClipboardCheck className="w-4 h-4" /> Start retrospective
            </button>
          )}
          {hasCompleted && (
            <>
              <button onClick={onViewResult} data-testid="tapi-view-result-btn"
                className="px-5 py-2.5 text-sm bg-brand hover:bg-brand-hover text-white font-medium rounded-sm flex items-center gap-2 transition-colors">
                View latest result <ArrowRight className="w-4 h-4" />
              </button>
              <button onClick={onRetake} data-testid="tapi-retake-btn"
                className="px-4 py-2.5 text-sm border border-ink-line hover:bg-secondary rounded-sm flex items-center gap-2 transition-colors">
                <RotateCcw className="w-4 h-4" /> Retake
              </button>
            </>
          )}
        </div>
        {hasDraft && <div className="mt-4 text-xs text-ink-muted">Draft in progress — {Object.keys(assessment.answers || {}).length} of 10 answered.</div>}
      </div>
      <div className="card-flat p-6">
        <div className="stat-label">How it works</div>
        <ol className="text-sm space-y-2 mt-3 list-decimal pl-4 text-ink-muted">
          <li>Questions are mixed: radio (one option) and checkbox (multiple).</li>
          <li>Q3 and Q4 reward thoroughness — ticking more boxes shows reflective analysis.</li>
          <li>Q6/Q7/Q8 ask retrospectively how accurate your BNBD/PCEI/PCAB predictions were.</li>
          <li>Scores are hidden while answering. Results show 5-band tier + dimensional bars.</li>
        </ol>
        {archivedCount > 0 && (
          <div className="mt-6 pt-4 border-t border-ink-line text-xs text-ink-muted flex items-center gap-2" data-testid="tapi-archived-count">
            <Archive className="w-3 h-3" /> {archivedCount} archived attempt{archivedCount === 1 ? "" : "s"}
          </div>
        )}
      </div>
    </div>
  );
}

function QuizView({ schema, currentQ, qIdx, totalQ, dimensionLabel, selectedFor, selectSingle, toggleMulti,
                   saving, submitting, goNext, goPrev, onSubmit, ctx, onJump, answers }) {
  const isMulti = currentQ.type === "multi";
  const sel = selectedFor(currentQ.code);
  const answeredCount = (schema?.questions || []).filter((q) => {
    if (q.type === "multi") return q.code in (answers || {});
    return (answers || {})[q.code] !== undefined;
  }).length;
  // For "complete enough to submit", require all single-select answered
  const singleAnswered = (schema?.questions || []).filter((q) => q.type === "single")
    .every((q) => Array.isArray(answers[q.code]) && answers[q.code].length > 0);

  // Auto-population banner for Q6/Q8
  const showAutoBanner = (currentQ.code === "Q6" && typeof ctx?.suggestions?.Q6 === "number")
                      || (currentQ.code === "Q8" && typeof ctx?.suggestions?.Q8 === "number");
  const autoSourceLabel = currentQ.code === "Q6"
    ? `BNBD result (${ctx?.prior_assessments?.bnbd?.tier_label || "—"})`
    : `PCAB result (${ctx?.prior_assessments?.pcab?.tier_label || "—"})`;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 card-flat p-7" data-testid="tapi-quiz">
        <div className="flex items-center justify-between mb-4">
          <div className="text-[11px] uppercase tracking-[0.2em] text-ink-muted">
            {dimensionLabel} · {currentQ.code} of 10
          </div>
          <div className="text-[11px] uppercase tracking-[0.2em] font-num text-ink-muted" data-testid="tapi-progress-text">
            {qIdx + 1} / {totalQ} · {isMulti ? "Multi-select" : "Single-select"}
          </div>
        </div>
        <div className="h-1 bg-secondary rounded-sm overflow-hidden">
          <div className="h-full bg-brand transition-all" style={{ width: `${((qIdx + 1) / totalQ) * 100}%` }} data-testid="tapi-progress-bar" />
        </div>

        <h3 className="font-display text-xl font-bold tracking-tight mt-6" data-testid="tapi-q-label">
          {currentQ.label}
        </h3>
        {isMulti && <div className="text-xs text-ink-muted mt-1">Choose all that apply. Continue is enabled even with zero selections.</div>}

        {showAutoBanner && (
          <div className="mt-4 px-3 py-2 rounded-sm border border-brand/30 bg-brand-soft text-[12px] text-ink flex items-start gap-2" data-testid={`tapi-auto-banner-${currentQ.code}`}>
            <Info className="w-3.5 h-3.5 text-brand shrink-0 mt-0.5" />
            <span>Suggested based on your prior <strong>{autoSourceLabel}</strong>. You can override.</span>
          </div>
        )}

        <div className="mt-5 space-y-2.5">
          {currentQ.options.map((o, i) => {
            const isSel = sel.includes(i);
            return (
              <button
                key={`${currentQ.code}-${i}`}
                type="button"
                onClick={() => isMulti ? toggleMulti(currentQ.code, i) : selectSingle(currentQ.code, i)}
                data-testid={`tapi-${currentQ.code}-opt-${i}`}
                className={`w-full flex items-center gap-3 text-left px-4 py-3 text-sm rounded-sm border transition-colors ${
                  isSel ? "border-brand bg-brand/5 text-ink" : "border-ink-line bg-white hover:bg-secondary text-ink-muted hover:text-ink"
                }`}
              >
                <span className={`w-5 h-5 ${isMulti ? "rounded-sm" : "rounded-full"} border flex items-center justify-center shrink-0 ${
                  isSel ? "border-brand bg-brand text-white" : "border-ink-line"
                }`}>
                  {isSel && <Check className="w-3 h-3" />}
                </span>
                <span>{o.label}</span>
              </button>
            );
          })}
        </div>

        {isMulti && (
          <div className="mt-3 text-xs text-ink-muted" data-testid={`tapi-${currentQ.code}-count`}>
            {sel.length} selected
          </div>
        )}

        <div className="mt-7 pt-5 border-t border-ink-line flex items-center justify-between flex-wrap gap-3">
          <button onClick={goPrev} disabled={qIdx === 0} data-testid="tapi-back-btn"
            className="px-4 py-2 text-sm border border-ink-line hover:bg-secondary rounded-sm flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <div className="text-xs text-ink-muted">
            {saving && <span className="flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin" /> Saving…</span>}
            {!saving && !isMulti && sel.length === 0 && <span>Pick an option to continue.</span>}
            {!saving && (isMulti || sel.length > 0) && <span>Saved.</span>}
          </div>
          {qIdx < totalQ - 1 ? (
            <button onClick={goNext} disabled={!isMulti && sel.length === 0} data-testid="tapi-next-btn"
              className="px-5 py-2 text-sm bg-brand hover:bg-brand-hover disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium rounded-sm flex items-center gap-1.5 transition-colors">
              Continue <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button onClick={onSubmit} disabled={!singleAnswered || submitting} data-testid="tapi-submit-btn"
              className="px-5 py-2 text-sm bg-brand hover:bg-brand-hover disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium rounded-sm flex items-center gap-1.5 transition-colors">
              {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</> : <>Submit <Check className="w-4 h-4" /></>}
            </button>
          )}
        </div>
      </div>

      <div className="card-flat p-6">
        <div className="stat-label">Progress</div>
        <div className="font-num text-5xl font-bold mt-2 text-brand">
          {answeredCount}<span className="text-ink-muted">/{totalQ}</span>
        </div>
        <div className="text-[11px] uppercase tracking-wider text-ink-muted mt-1">Answered</div>
        <div className="mt-5 space-y-3" data-testid="tapi-dots">
          {(schema?.dimensions || []).map((dim) => (
            <div key={dim.id}>
              <div className="text-[10px] uppercase tracking-wider text-ink-muted mb-1">{dim.label}</div>
              <div className="flex flex-wrap gap-1.5">
                {(schema.questions || []).filter((q) => q.dimension === dim.id).map((q) => {
                  const idx = (schema.questions || []).findIndex((x) => x.code === q.code);
                  const answered = q.code in (answers || {});
                  const isCurrent = idx === qIdx;
                  return (
                    <button key={q.code} onClick={() => onJump(idx)} data-testid={`tapi-dot-${q.code}`}
                      className={`w-10 h-8 text-[10px] font-num rounded-sm transition-all ${
                        isCurrent ? "bg-brand text-white ring-2 ring-brand/30"
                                  : answered ? "bg-good text-white hover:bg-good/80"
                                             : "bg-secondary text-ink-muted hover:bg-ink-line"
                      }`}>
                      {q.code}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ResultsView({ assessment, schema, onRetake }) {
  const a = assessment;
  const pill = PILL_FOR(a.tier_color);
  const action = (schema?.action_for_tier || {})[a.tier_label];
  const lessons = a.lessons || { root_causes: [], pricing_misses: [] };
  return (
    <div className="space-y-6" data-testid="tapi-results">
      <div className={`card-flat p-8 border-l-4 ${pill.border}`}>
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="stat-label">TAPI Performance</div>
            <div className="flex items-baseline gap-2 mt-2">
              <div className="font-num text-7xl font-bold leading-none">{a.total_score}</div>
              <div className="text-2xl font-num text-ink-muted">/ 100</div>
            </div>
            <div className="mt-4 flex items-center gap-3 flex-wrap">
              <span className={`px-3 py-1 text-sm font-display font-bold rounded-sm border ${pill.bg} ${pill.text} ${pill.border}`} data-testid="tapi-tier-chip">
                {a.tier_label}
              </span>
              {a.result_position && (
                <span className="chip text-xs border-ink-line bg-secondary text-ink-muted" data-testid="tapi-result-position">
                  Result: {a.result_position}
                </span>
              )}
            </div>
            {action && <p className="text-sm text-ink-muted mt-3 max-w-prose">{action}</p>}
          </div>
          <button onClick={onRetake} data-testid="tapi-result-retake-btn"
            className="px-4 py-2 text-sm border border-ink-line hover:bg-secondary rounded-sm flex items-center gap-2 transition-colors">
            <RotateCcw className="w-4 h-4" /> Retake
          </button>
        </div>
      </div>

      <div className="card-flat p-6">
        <div className="stat-label">Dimensional breakdown</div>
        <div className="space-y-3 mt-4" data-testid="tapi-breakdown">
          {(a.dimension_breakdown || []).map((d) => {
            const flagPill = PILL_FOR(d.flag_color);
            return (
              <div key={d.id} className="space-y-1" data-testid={`tapi-dim-${d.id}`}>
                <div className="flex items-baseline justify-between text-sm">
                  <span className="font-medium">{d.label}</span>
                  <span className="flex items-center gap-2">
                    <span className="font-num text-ink-muted" data-testid={`tapi-dim-pct-${d.id}`}>{Math.round((d.score / d.max_score) * 100)}%</span>
                    <span className={`chip text-[10px] ${flagPill.bg} ${flagPill.text} ${flagPill.border}`}>{d.flag_label}</span>
                  </span>
                </div>
                <div className="h-2 bg-secondary rounded-sm overflow-hidden">
                  <div className={`h-full transition-all ${BAR_FOR(d.flag_color)}`} style={{ width: `${Math.min(100, d.pct)}%` }} />
                </div>
                <div className="text-[11px] text-ink-muted">{d.description}</div>
              </div>
            );
          })}
        </div>
      </div>

      {a.outcome_summary && (
        <div className="card-flat p-5 text-sm leading-relaxed" data-testid="tapi-outcome-summary">
          {a.outcome_summary.split("**").map((p, i) => i % 2 === 1 ? <strong key={i}>{p}</strong> : <span key={i}>{p}</span>)}
        </div>
      )}

      {Array.isArray(a.diagnostic_summary) && a.diagnostic_summary.length > 0 && (
        <div className="card-flat p-6" data-testid="tapi-diagnostic-summary">
          <div className="stat-label">Diagnostic accuracy</div>
          <div className="space-y-3 mt-3">
            {a.diagnostic_summary.map((row) => (
              <div key={row.code} className="text-sm flex items-baseline justify-between gap-3 border-b border-ink-line last:border-b-0 pb-2 last:pb-0">
                <div>
                  <div className="font-display font-bold text-sm">{row.framework} · {row.rating_text}</div>
                </div>
                <div className="font-num text-sm font-bold text-brand whitespace-nowrap">{row.score} / {row.max}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {(lessons.root_causes.length > 0 || lessons.pricing_misses.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {lessons.root_causes.length > 0 && (
            <div className="card-flat p-6" data-testid="tapi-root-causes">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-warn" />
                <div className="stat-label">Root causes identified</div>
              </div>
              <ul className="mt-3 space-y-1.5">
                {lessons.root_causes.map((c) => (
                  <li key={c} className="text-sm text-ink flex items-center gap-2">
                    <span className="w-1 h-1 rounded-full bg-warn" /> {c}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {lessons.pricing_misses.length > 0 && (
            <div className="card-flat p-6" data-testid="tapi-pricing-misses">
              <div className="flex items-center gap-2">
                <Trophy className="w-4 h-4 text-brand" />
                <div className="stat-label">Pricing assumption misses</div>
              </div>
              <ul className="mt-3 space-y-1.5">
                {lessons.pricing_misses.map((c) => (
                  <li key={c} className="text-sm text-ink flex items-center gap-2">
                    <span className="w-1 h-1 rounded-full bg-brand" /> {c}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <div className="text-[11px] uppercase tracking-wider text-ink-muted">
        Completed {a.completed_at ? new Date(a.completed_at).toLocaleString() : "—"}
      </div>
    </div>
  );
}

function RetakeModal({ previousScore, previousTier, onConfirm, onClose }) {
  return (
    <div className="fixed inset-0 bg-ink/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="tapi-retake-modal">
      <div className="bg-white max-w-md w-full border border-ink-line rounded-sm" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-ink-line">
          <div className="font-display font-bold tracking-tight">Retake TAPI assessment?</div>
        </div>
        <div className="p-6 text-sm space-y-3 text-ink-muted">
          <p>The current attempt will be archived and a fresh 10-question assessment will start.</p>
          {Number.isFinite(previousScore) && (
            <p>Previous result: <strong className="text-ink font-num">{previousScore} / 100</strong>{previousTier && <> · <strong className="text-ink">{previousTier}</strong></>}</p>
          )}
        </div>
        <div className="px-6 py-4 border-t border-ink-line flex items-center justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-ink-line hover:bg-secondary rounded-sm transition-colors" data-testid="tapi-retake-cancel">Cancel</button>
          <button onClick={onConfirm} className="px-5 py-2 text-sm bg-brand hover:bg-brand-hover text-white font-medium rounded-sm flex items-center gap-2 transition-colors" data-testid="tapi-retake-confirm">
            <RotateCcw className="w-4 h-4" /> Retake
          </button>
        </div>
      </div>
    </div>
  );
}
